import React, { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatRelativeTime } from "../utils/dateFormat";
import {
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, Loader2, RefreshCw, Plus, FileText,
  Clock, Target, Search, Filter, ArrowRight, Eye, BarChart3,
  ListChecks, Wrench, Link2, Activity, TrendingUp, Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== CONSTANTS ====================

const STATUS_COLORS = {
  compliant:           { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Compliant" },
  partially_compliant: { bg: "bg-amber-500/10", text: "text-amber-400", label: "Partial" },
  non_compliant:       { bg: "bg-red-500/10", text: "text-red-400", label: "Non-Compliant" },
  not_applicable:      { bg: "bg-[#2D313D]", text: "text-[#666]", label: "N/A" },
  not_evaluated:       { bg: "bg-[#1E2230]", text: "text-[#555]", label: "Not Evaluated" },
};

const SEVERITY_COLORS = {
  critical: { bg: "bg-red-500/10", text: "text-red-400" },
  high:     { bg: "bg-orange-500/10", text: "text-orange-400" },
  medium:   { bg: "bg-amber-500/10", text: "text-amber-400" },
  low:      { bg: "bg-green-500/10", text: "text-green-400" },
};

const BAND_COLORS = {
  "Audit Ready":       { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
  "Nearly Ready":      { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30" },
  "Gaps Exist":        { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
  "Significant Gaps":  { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  "Not Ready":         { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30" },
};

const REM_STATUS_COLORS = {
  planned:      { bg: "bg-sky-500/10", text: "text-sky-400" },
  in_progress:  { bg: "bg-amber-500/10", text: "text-amber-400" },
  implemented:  { bg: "bg-green-500/10", text: "text-green-400" },
  verified:     { bg: "bg-emerald-500/10", text: "text-emerald-400" },
  closed:       { bg: "bg-[#2D313D]", text: "text-[#666]" },
  deferred:     { bg: "bg-[#2D313D]", text: "text-[#8B95A5]" },
};

// ==================== SCORE RING COMPONENT ====================

function ScoreRing({ score, band, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  const bandStyle = BAND_COLORS[band] || BAND_COLORS["Not Ready"];

  const colorMap = {
    "text-emerald-400": "#34D399", "text-green-400": "#4ADE80",
    "text-amber-400": "#FBBF24", "text-orange-400": "#FB923C",
    "text-red-400": "#F87171",
  };
  const stroke = colorMap[bandStyle.text] || "#F87171";

  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#1E2230" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={stroke} strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
        <text x={size/2} y={size/2 - 4} textAnchor="middle" fill="white" fontSize="24" fontWeight="800">{score}%</text>
        <text x={size/2} y={size/2 + 16} textAnchor="middle" fill="#8B95A5" fontSize="10">{band}</text>
      </svg>
    </div>
  );
}

// ==================== SECTION HEATMAP ROW ====================

function SectionHeatmapRow({ section }) {
  const total = section.total;
  const pctCompliant = total ? Math.round((section.compliant / total) * 100) : 0;
  const pctPartial = total ? Math.round((section.partial / total) * 100) : 0;
  const pctNonCompliant = total ? Math.round((section.non_compliant / total) * 100) : 0;
  const pctNA = total ? Math.round((section.not_applicable / total) * 100) : 0;
  const pctNotEval = total ? Math.round((section.not_evaluated / total) * 100) : 0;

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-[11px] text-[#8B95A5] w-8 text-right font-mono">{section.section_number}</span>
      <span className="text-[11px] text-white w-40 truncate" title={section.title}>{section.title}</span>
      <div className="flex-1 flex h-3 rounded-full overflow-hidden bg-[#1E2230]">
        {pctCompliant > 0 && <div className="bg-emerald-500/60 h-full" style={{ width: `${pctCompliant}%` }} />}
        {pctPartial > 0 && <div className="bg-amber-500/60 h-full" style={{ width: `${pctPartial}%` }} />}
        {pctNonCompliant > 0 && <div className="bg-red-500/60 h-full" style={{ width: `${pctNonCompliant}%` }} />}
        {pctNA > 0 && <div className="bg-[#333] h-full" style={{ width: `${pctNA}%` }} />}
        {pctNotEval > 0 && <div className="bg-[#1A1C24] h-full" style={{ width: `${pctNotEval}%` }} />}
      </div>
      <span className={`text-[11px] font-mono w-12 text-right ${section.score >= 70 ? "text-emerald-400" : section.score >= 40 ? "text-amber-400" : "text-red-400"}`}>
        {section.score}%
      </span>
    </div>
  );
}

// ==================== REQUIREMENT ROW ====================

function RequirementRow({ req, evaluation, onEvaluate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: evaluation?.status || "not_evaluated",
    evidence_present: evaluation?.evidence_present || false,
    evidence_notes: evaluation?.evidence_notes || "",
    gap_description: evaluation?.gap_description || "",
    gap_severity: evaluation?.gap_severity || "medium",
    notes: evaluation?.notes || "",
  });

  const statusStyle = STATUS_COLORS[evaluation?.status] || STATUS_COLORS.not_evaluated;
  const sevStyle = SEVERITY_COLORS[req.criticality] || SEVERITY_COLORS.medium;

  const handleSave = () => {
    onEvaluate(req.req_id, form);
    setEditing(false);
  };

  return (
    <div className="border-b border-[rgba(255,255,255,0.04)] last:border-b-0" data-testid={`req-${req.req_id}`}>
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-[rgba(255,255,255,0.02)] cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown size={12} className="text-[#555] shrink-0" /> : <ChevronRight size={12} className="text-[#555] shrink-0" />}
        <span className="text-[10px] font-mono text-[#8B95A5] w-24 shrink-0">{req.req_id}</span>
        <span className="text-[11px] text-white flex-1 truncate">{req.title}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${sevStyle.bg} ${sevStyle.text} shrink-0`}>{req.criticality}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text} shrink-0 w-20 text-center`}>{statusStyle.label}</span>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1 ml-5 space-y-2">
          <p className="text-[11px] text-[#CBD5E1] leading-relaxed">{req.requirement}</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {req.expected_evidence?.map((ev, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#1E2230] text-[#8B95A5]">{ev}</span>
            ))}
          </div>
          {req.charstan_modules?.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1">
              <Link2 size={9} className="text-[#555]" />
              {req.charstan_modules.map((m, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400">{m}</span>
              ))}
            </div>
          )}

          {evaluation?.evaluated_at && (
            <p className="text-[9px] text-[#444]">Last evaluated: {formatRelativeTime(evaluation.evaluated_at)}</p>
          )}

          {editing ? (
            <div className="mt-2 p-3 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] space-y-2" data-testid={`eval-form-${req.req_id}`}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8B95A5] block mb-1">Status</label>
                  <select className="text-[11px] w-full px-2 py-1 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
                    value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="compliant">Compliant</option>
                    <option value="partially_compliant">Partially Compliant</option>
                    <option value="non_compliant">Non-Compliant</option>
                    <option value="not_applicable">Not Applicable</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#8B95A5] block mb-1">Gap Severity</label>
                  <select className="text-[11px] w-full px-2 py-1 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
                    value={form.gap_severity} onChange={e => setForm(f => ({ ...f, gap_severity: e.target.value }))}>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-[11px] text-[#8B95A5]">
                <input type="checkbox" checked={form.evidence_present} onChange={e => setForm(f => ({ ...f, evidence_present: e.target.checked }))}
                  className="rounded border-[rgba(255,255,255,0.1)]" />
                Evidence present
              </label>
              <textarea className="text-[11px] w-full px-2 py-1 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white h-14 resize-none"
                placeholder="Evidence notes..." value={form.evidence_notes} onChange={e => setForm(f => ({ ...f, evidence_notes: e.target.value }))} />
              {(form.status === "partially_compliant" || form.status === "non_compliant") && (
                <textarea className="text-[11px] w-full px-2 py-1 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white h-14 resize-none"
                  placeholder="Gap description..." value={form.gap_description} onChange={e => setForm(f => ({ ...f, gap_description: e.target.value }))} />
              )}
              <div className="flex items-center gap-2">
                <button className="text-[10px] px-3 py-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all"
                  onClick={handleSave} data-testid={`save-eval-${req.req_id}`}>Save Evaluation</button>
                <button className="text-[10px] px-3 py-1 rounded text-[#8B95A5] hover:text-white transition-all"
                  onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="text-[10px] px-2 py-1 rounded bg-[#1E2230] text-[#8B95A5] hover:text-white transition-all mt-1"
              onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`evaluate-btn-${req.req_id}`}>
              Evaluate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== GAP ROW ====================

function GapRow({ gap, onAddRemediation }) {
  const sevStyle = SEVERITY_COLORS[gap.gap_severity] || SEVERITY_COLORS.medium;
  return (
    <div className="flex items-start gap-3 p-3 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.04)]" data-testid={`gap-${gap.req_id}`}>
      <AlertTriangle size={14} className={`${sevStyle.text} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-mono text-[#8B95A5]">{gap.req_id}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${sevStyle.bg} ${sevStyle.text}`}>{gap.gap_severity}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS_COLORS[gap.evaluation_status]?.bg} ${STATUS_COLORS[gap.evaluation_status]?.text}`}>
            {STATUS_COLORS[gap.evaluation_status]?.label}
          </span>
        </div>
        <p className="text-[12px] text-white font-medium">{gap.title}</p>
        {gap.gap_description && <p className="text-[11px] text-[#8B95A5] mt-0.5">{gap.gap_description}</p>}
        <button
          className="text-[10px] px-2 py-0.5 rounded bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-all mt-2"
          onClick={() => onAddRemediation(gap)}
          data-testid={`add-rem-${gap.req_id}`}
        >
          <Plus size={9} className="inline mr-1" />Add Remediation
        </button>
      </div>
    </div>
  );
}

// ==================== REMEDIATION ROW ====================

function RemediationRow({ rem, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(rem.status);
  const remStyle = REM_STATUS_COLORS[rem.status] || REM_STATUS_COLORS.planned;
  const sevStyle = SEVERITY_COLORS[rem.priority] || SEVERITY_COLORS.medium;

  const handleStatusUpdate = async (newStatus) => {
    await onUpdate(rem.remediation_id, { status: newStatus });
    setStatus(newStatus);
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.04)]" data-testid={`rem-${rem.remediation_id}`}>
      <Wrench size={14} className="text-sky-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] font-mono text-[#8B95A5]">{rem.remediation_id}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${sevStyle.bg} ${sevStyle.text}`}>{rem.priority}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${remStyle.bg} ${remStyle.text}`}>{status}</span>
          {rem.due_date && <span className="text-[9px] text-[#555]">Due: {rem.due_date}</span>}
        </div>
        <p className="text-[12px] text-white font-medium">{rem.title}</p>
        {rem.description && <p className="text-[11px] text-[#8B95A5] mt-0.5">{rem.description}</p>}
        {rem.owner_name && <p className="text-[10px] text-[#555] mt-1">Owner: {rem.owner_name}</p>}

        {editing ? (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {["planned", "in_progress", "implemented", "verified", "closed", "deferred"].map(s => (
              <button key={s} className={`text-[9px] px-2 py-0.5 rounded border border-[rgba(255,255,255,0.08)] ${status === s ? "bg-sky-500/20 text-sky-400" : "text-[#8B95A5] hover:text-white"} transition-all`}
                onClick={() => handleStatusUpdate(s)}>{s}</button>
            ))}
            <button className="text-[9px] px-2 py-0.5 text-[#555]" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        ) : (
          <button className="text-[10px] px-2 py-0.5 rounded bg-[#1E2230] text-[#8B95A5] hover:text-white transition-all mt-2"
            onClick={() => setEditing(true)} data-testid={`update-rem-${rem.remediation_id}`}>Update Status</button>
        )}
      </div>
    </div>
  );
}

// ==================== ADD REMEDIATION MODAL ====================

function AddRemediationForm({ gap, assessmentId, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: `Remediate: ${gap.title}`,
    description: gap.gap_description || "",
    owner_name: "",
    priority: gap.gap_severity || "medium",
    due_date: "",
  });

  const handleSubmit = () => {
    onSubmit({ ...form, req_id: gap.req_id });
  };

  return (
    <div className="p-4 rounded-lg bg-[#0D0F14] border border-[rgba(255,255,255,0.08)] space-y-3" data-testid="add-remediation-form">
      <p className="text-sm font-semibold text-white">New Remediation Action</p>
      <p className="text-[10px] text-[#8B95A5]">For: {gap.req_id} — {gap.title}</p>
      <input className="text-[11px] w-full px-2 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
        placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
      <textarea className="text-[11px] w-full px-2 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white h-16 resize-none"
        placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      <div className="grid grid-cols-3 gap-2">
        <input className="text-[11px] px-2 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
          placeholder="Owner" value={form.owner_name} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} />
        <select className="text-[11px] px-2 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
          value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <input type="date" className="text-[11px] px-2 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
          value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
      </div>
      <div className="flex items-center gap-2">
        <button className="text-[10px] px-3 py-1.5 rounded bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-all"
          onClick={handleSubmit} data-testid="submit-remediation">Create Remediation</button>
        <button className="text-[10px] px-3 py-1.5 text-[#8B95A5] hover:text-white transition-all" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================

export default function Annex11ReadinessPage() {
  const { user } = useAuth();
  const { t } = useTranslation('platform');

  const [assessments, setAssessments] = useState([]);
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [sections, setSections] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [remediations, setRemediations] = useState([]);
  const [soaImpact, setSoaImpact] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [addRemGap, setAddRemGap] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", scope: "", assessor_name: "" });

  const setLoadingKey = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  // Export PDF
  const exportPdf = async () => {
    if (!activeAssessment?.assessment_id) return;
    setLoadingKey("export", true);
    try {
      const res = await fetch(
        `${API}/api/governance/annex11/assessments/${activeAssessment.assessment_id}/export/pdf`,
        { credentials: "include" }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `CHARSTAN_Annex11_${activeAssessment.assessment_id}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setLoadingKey("export", false);
  };

  // Fetch requirements catalog on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/governance/annex11/requirements`, { credentials: "include" });
        if (res.ok) {
          const d = await res.json();
          setRequirements(d.requirements || []);
          setSections(d.sections || []);
        }
      } catch {}
    })();
  }, []);

  // Fetch assessments on mount
  const loadAssessments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/governance/annex11/assessments`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setAssessments(d.assessments || []);
        if (d.assessments?.length > 0 && !activeAssessment) {
          setActiveAssessment(d.assessments[0]);
        }
      }
    } catch {}
  }, [activeAssessment]);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  // Load assessment data when active assessment changes
  const loadAssessmentData = useCallback(async (assessmentId) => {
    if (!assessmentId) return;
    setLoadingKey("data", true);

    try {
      const [readinessRes, gapsRes, remsRes, soaRes] = await Promise.all([
        fetch(`${API}/api/governance/annex11/assessments/${assessmentId}/readiness`, { credentials: "include" }),
        fetch(`${API}/api/governance/annex11/assessments/${assessmentId}/gaps`, { credentials: "include" }),
        fetch(`${API}/api/governance/annex11/assessments/${assessmentId}/remediation`, { credentials: "include" }),
        fetch(`${API}/api/governance/annex11/assessments/${assessmentId}/soa-impact`, { credentials: "include" }),
      ]);

      if (readinessRes.ok) setReadiness(await readinessRes.json());
      if (gapsRes.ok) { const d = await gapsRes.json(); setGaps(d.gaps || []); }
      if (remsRes.ok) { const d = await remsRes.json(); setRemediations(d.remediations || []); }
      if (soaRes.ok) setSoaImpact(await soaRes.json());
    } catch {}
    setLoadingKey("data", false);
  }, []);

  useEffect(() => {
    if (activeAssessment?.assessment_id) {
      loadAssessmentData(activeAssessment.assessment_id);
    }
  }, [activeAssessment, loadAssessmentData]);

  // Create assessment
  const createAssessment = async () => {
    if (!createForm.title.trim()) return;
    setLoadingKey("create", true);
    try {
      const res = await fetch(`${API}/api/governance/annex11/assessments`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        const d = await res.json();
        setAssessments(p => [d, ...p]);
        setActiveAssessment(d);
        setShowCreate(false);
        setCreateForm({ title: "", scope: "", assessor_name: "" });
      }
    } catch {}
    setLoadingKey("create", false);
  };

  // Evaluate requirement
  const evaluateRequirement = async (reqId, form) => {
    if (!activeAssessment) return;
    try {
      const res = await fetch(`${API}/api/governance/annex11/assessments/${activeAssessment.assessment_id}/evaluate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ req_id: reqId, ...form }),
      });
      if (res.ok) {
        // Reload assessment to get updated evaluations
        const aRes = await fetch(`${API}/api/governance/annex11/assessments/${activeAssessment.assessment_id}`, { credentials: "include" });
        if (aRes.ok) setActiveAssessment(await aRes.json());
        loadAssessmentData(activeAssessment.assessment_id);
      }
    } catch {}
  };

  // Create remediation
  const createRemediation = async (form) => {
    if (!activeAssessment) return;
    try {
      const res = await fetch(`${API}/api/governance/annex11/assessments/${activeAssessment.assessment_id}/remediation`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setAddRemGap(null);
        loadAssessmentData(activeAssessment.assessment_id);
      }
    } catch {}
  };

  // Update remediation
  const updateRemediation = async (remId, updates) => {
    try {
      await fetch(`${API}/api/governance/annex11/remediation/${remId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      loadAssessmentData(activeAssessment.assessment_id);
    } catch {}
  };

  // Filter requirements
  const filteredReqs = requirements.filter(r => {
    if (searchTerm && !r.title.toLowerCase().includes(searchTerm.toLowerCase()) && !r.req_id.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterStatus !== "all") {
      const ev = activeAssessment?.evaluations?.[r.req_id];
      if (filterStatus === "gaps" && !["non_compliant", "partially_compliant"].includes(ev?.status)) return false;
      if (filterStatus !== "gaps" && ev?.status !== filterStatus) return false;
    }
    return true;
  });

  const TABS = [
    { id: "overview", label: t('annex11.overallScore').split(' ')[0] || "Overview", icon: BarChart3 },
    { id: "requirements", label: t('annex11.sections'), icon: ListChecks },
    { id: "gaps", label: t('annex11.findings'), icon: AlertTriangle },
    { id: "remediation", label: t('annex11.remediations'), icon: Wrench },
    { id: "soa-impact", label: "SoA Impact", icon: Link2 },
  ];

  return (
    <QMSLayout title={t('annex11.title')}>
      <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="annex11-readiness-page">

        {/* ==================== HEADER ==================== */}
        <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-4" data-testid="annex11-header">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500/20 to-sky-500/5 flex items-center justify-center">
                <Shield size={20} className="text-sky-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">{t('annex11.title')}</h1>
                <p className="text-[11px] text-[#8B95A5]">{t('annex11.subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Assessment selector */}
              <select className="text-[11px] px-3 py-1.5 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white focus:outline-none max-w-[240px]"
                value={activeAssessment?.assessment_id || ""}
                onChange={e => {
                  const a = assessments.find(a => a.assessment_id === e.target.value);
                  if (a) setActiveAssessment(a);
                }}
                data-testid="assessment-selector">
                {assessments.map(a => (
                  <option key={a.assessment_id} value={a.assessment_id}>{a.title} ({a.status})</option>
                ))}
              </select>

              <button className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 transition-all"
                onClick={() => setShowCreate(!showCreate)} data-testid="new-assessment-btn">
                <Plus size={12} />New Assessment
              </button>

              {activeAssessment && (user?.role === "admin" || user?.role === "auditor") && (
                <button
                  className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all disabled:opacity-40"
                  onClick={exportPdf}
                  disabled={loading.export}
                  data-testid="export-pdf-btn"
                >
                  {loading.export ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Export PDF
                </button>
              )}

              <button className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-[#1E2230] text-[#8B95A5] hover:text-white transition-all"
                onClick={() => activeAssessment && loadAssessmentData(activeAssessment.assessment_id)} data-testid="refresh-btn">
                <RefreshCw size={12} />
              </button>
            </div>
          </div>

          {showCreate && (
            <div className="mt-4 p-4 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] space-y-2" data-testid="create-assessment-form">
              <input className="text-[11px] w-full px-3 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
                placeholder="Assessment title" value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} />
              <div className="grid grid-cols-2 gap-2">
                <input className="text-[11px] px-3 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
                  placeholder="Scope" value={createForm.scope} onChange={e => setCreateForm(f => ({ ...f, scope: e.target.value }))} />
                <input className="text-[11px] px-3 py-1.5 rounded bg-[#14161C] border border-[rgba(255,255,255,0.08)] text-white"
                  placeholder="Assessor name" value={createForm.assessor_name} onChange={e => setCreateForm(f => ({ ...f, assessor_name: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <button className="text-[10px] px-3 py-1.5 rounded bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 disabled:opacity-40"
                  onClick={createAssessment} disabled={loading.create || !createForm.title.trim()} data-testid="submit-assessment">
                  {loading.create ? <Loader2 size={10} className="animate-spin inline mr-1" /> : null}Create
                </button>
                <button className="text-[10px] px-3 py-1.5 text-[#8B95A5]" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* ==================== TAB BAR ==================== */}
        <div className="flex items-center gap-0 border-b border-[rgba(255,255,255,0.06)]">
          {TABS.map(tab => (
            <button key={tab.id}
              className={`flex items-center gap-1.5 text-[11px] px-4 py-2.5 border-b-2 transition-all ${activeTab === tab.id ? "border-sky-400 text-sky-400" : "border-transparent text-[#8B95A5] hover:text-white"}`}
              onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}>
              <tab.icon size={13} />{tab.label}
              {tab.id === "gaps" && gaps.length > 0 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 font-bold">{gaps.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ==================== TAB CONTENT ==================== */}
        {loading.data ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={28} className="animate-spin text-sky-400" /></div>
        ) : !activeAssessment ? (
          <div className="text-center py-16">
            <Shield size={32} className="text-[#2D313D] mx-auto mb-3" />
            <p className="text-[13px] text-[#555]">No assessments yet</p>
            <p className="text-[11px] text-[#444] mt-1">Create a new assessment to start your Annex 11 readiness review</p>
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && readiness && (
              <div className="space-y-5" data-testid="overview-tab">
                {/* Top stats */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="lg:col-span-1 rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-5 flex flex-col items-center justify-center">
                    <ScoreRing score={readiness.overall_score} band={readiness.readiness_band} />
                    <p className="text-[11px] text-[#8B95A5] mt-2">Overall Readiness</p>
                  </div>
                  <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: "Total Requirements", value: readiness.total_requirements, color: "text-white" },
                      { label: "Applicable", value: readiness.total_applicable, color: "text-sky-400" },
                      { label: "Total Gaps", value: readiness.gap_count, color: readiness.gap_count > 0 ? "text-amber-400" : "text-emerald-400" },
                      { label: "Critical Gaps", value: readiness.critical_gaps, color: readiness.critical_gaps > 0 ? "text-red-400" : "text-emerald-400" },
                      { label: "Remediation Total", value: readiness.remediation_total, color: "text-sky-400" },
                      { label: "Remediation Closed", value: readiness.remediation_closed, color: "text-emerald-400" },
                      { label: "Assessment Status", value: activeAssessment.status, color: "text-[#E8A838]" },
                      { label: "Assessor", value: activeAssessment.assessor_name || "—", color: "text-[#8B95A5]" },
                    ].map((stat, i) => (
                      <div key={i} className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-3">
                        <p className="text-[10px] text-[#8B95A5] uppercase tracking-wide">{stat.label}</p>
                        <p className={`text-lg font-bold ${stat.color} mt-1`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section heatmap */}
                <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-4">
                  <p className="text-sm font-semibold text-white mb-3">Section Compliance Heatmap</p>
                  <div className="flex items-center gap-3 mb-3 text-[9px] text-[#8B95A5]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500/60" />Compliant</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500/60" />Partial</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500/60" />Non-Compliant</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#333]" />N/A</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#1A1C24]" />Not Evaluated</span>
                  </div>
                  <div className="space-y-0.5" data-testid="section-heatmap">
                    {readiness.section_breakdown?.map(s => <SectionHeatmapRow key={s.section_id} section={s} />)}
                  </div>
                </div>
              </div>
            )}

            {/* REQUIREMENTS TAB */}
            {activeTab === "requirements" && (
              <div className="space-y-3" data-testid="requirements-tab">
                {/* Search + filter */}
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#555]" />
                    <input className="text-[11px] w-full pl-7 pr-3 py-1.5 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white"
                      placeholder="Search requirements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="req-search" />
                  </div>
                  <select className="text-[11px] px-3 py-1.5 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white"
                    value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="req-filter">
                    <option value="all">All Status</option>
                    <option value="compliant">Compliant</option>
                    <option value="partially_compliant">Partial</option>
                    <option value="non_compliant">Non-Compliant</option>
                    <option value="not_applicable">N/A</option>
                    <option value="not_evaluated">Not Evaluated</option>
                    <option value="gaps">Gaps Only</option>
                  </select>
                  <span className="text-[10px] text-[#8B95A5]">{filteredReqs.length} of {requirements.length}</span>
                </div>

                <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] divide-y divide-[rgba(255,255,255,0.04)]">
                  {filteredReqs.map(r => (
                    <RequirementRow key={r.req_id} req={r}
                      evaluation={activeAssessment?.evaluations?.[r.req_id]}
                      onEvaluate={evaluateRequirement} />
                  ))}
                  {filteredReqs.length === 0 && (
                    <p className="text-[11px] text-[#555] py-8 text-center">No requirements match the current filter</p>
                  )}
                </div>
              </div>
            )}

            {/* GAP REGISTER TAB */}
            {activeTab === "gaps" && (
              <div className="space-y-3" data-testid="gaps-tab">
                {gaps.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 size={28} className="text-emerald-400/30 mx-auto mb-2" />
                    <p className="text-[12px] text-[#555]">No gaps identified</p>
                    <p className="text-[10px] text-[#444] mt-1">Evaluate requirements to populate the gap register</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {gaps.map(g => <GapRow key={g.req_id} gap={g} onAddRemediation={setAddRemGap} />)}
                  </div>
                )}
                {addRemGap && (
                  <AddRemediationForm gap={addRemGap} assessmentId={activeAssessment.assessment_id}
                    onSubmit={createRemediation} onCancel={() => setAddRemGap(null)} />
                )}
              </div>
            )}

            {/* REMEDIATION TAB */}
            {activeTab === "remediation" && (
              <div className="space-y-3" data-testid="remediation-tab">
                {remediations.length === 0 ? (
                  <div className="text-center py-12">
                    <Wrench size={28} className="text-[#2D313D] mx-auto mb-2" />
                    <p className="text-[12px] text-[#555]">No remediation actions yet</p>
                    <p className="text-[10px] text-[#444] mt-1">Add remediations from the Gap Register tab</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {remediations.map(r => <RemediationRow key={r.remediation_id} rem={r} onUpdate={updateRemediation} />)}
                  </div>
                )}
              </div>
            )}

            {/* SOA IMPACT TAB */}
            {activeTab === "soa-impact" && (
              <div className="space-y-3" data-testid="soa-impact-tab">
                {!soaImpact || soaImpact.impacts?.length === 0 ? (
                  <div className="text-center py-12">
                    <Link2 size={28} className="text-[#2D313D] mx-auto mb-2" />
                    <p className="text-[12px] text-[#555]">No SoA linkages found</p>
                    <p className="text-[10px] text-[#444] mt-1">Link SoA entries during requirement evaluation</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-[#8B95A5] border-b border-[rgba(255,255,255,0.06)]">
                          <th className="text-left py-2 px-3 font-medium">Req ID</th>
                          <th className="text-left py-2 px-3 font-medium">Requirement</th>
                          <th className="text-left py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">SoA Entry</th>
                          <th className="text-left py-2 px-3 font-medium">SoA Status</th>
                          <th className="text-left py-2 px-3 font-medium">Impact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soaImpact.impacts.map((imp, i) => (
                          <tr key={i} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
                            <td className="py-1.5 px-3 font-mono text-[#8B95A5]">{imp.req_id}</td>
                            <td className="py-1.5 px-3 text-white">{imp.req_title}</td>
                            <td className="py-1.5 px-3">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS_COLORS[imp.evaluation_status]?.bg} ${STATUS_COLORS[imp.evaluation_status]?.text}`}>
                                {STATUS_COLORS[imp.evaluation_status]?.label}
                              </span>
                            </td>
                            <td className="py-1.5 px-3 text-sky-400">{imp.soa_control_code || imp.soa_entry_id}</td>
                            <td className="py-1.5 px-3 text-[#8B95A5]">{imp.soa_gap_status || "—"}</td>
                            <td className="py-1.5 px-3 text-[#CBD5E1]">{imp.impact_note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </QMSLayout>
  );
}
